import { useCallback, useEffect, useState } from 'react';
import { ErrorLine } from '../../components/Lines';
import { errorText } from '../../errors';
import { t } from '../../i18n';

// What the events screens ask of the server as they open (B3): the list, or the event, while it's
// on its way, once it came, or what went wrong, with the way to try again (A5's screens' way).

export type Load<T> =
  { status: 'loading' } | { status: 'failed'; error: unknown } | { status: 'loaded'; value: T };

/**
 * What `load` answers, asked as the screen opens and again at `retry()`. `load` must stay the same
 * function from render to render (a module's, or one made with useCallback).
 */
export function useLoad<T>(load: () => Promise<T>): { state: Load<T>; retry: () => void } {
  const [state, setState] = useState<Load<T>>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    load().then(
      (value) => {
        if (current) setState({ status: 'loaded', value });
      },
      (error: unknown) => {
        if (current) setState({ status: 'failed', error });
      },
    );
    return () => {
      current = false;
    };
  }, [load, attempt]);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  return { state, retry };
}

/** What hasn't come (yet): loading, or failed. */
export type NotLoaded = Exclude<Load<unknown>, { status: 'loaded' }>;

/** While it loads, the line saying so; if it failed, what went wrong and Try again. */
export function Unloaded({ state, onRetry }: { state: NotLoaded; onRetry: () => void }) {
  if (state.status === 'loading') return <p className="muted">{t('app.loading')}</p>;
  return (
    <div className="stack">
      <ErrorLine text={errorText(state.error)} />
      <button type="button" className="button" onClick={onRetry}>
        {t('app.retry')}
      </button>
    </div>
  );
}
